import { debounce } from "lodash";
import React from "react";
import { observer } from "mobx-react";
import {
  PricingPageInputModel,
  PricePageTermsInputModel,
  PricePageRecurringInputModel,
  ItemGridModel,
} from "../../../shared/models";
import { NavigateFunction, Params } from "react-router-dom";
import TrioBallsIcon from "../../../../../../images/common/ball-trio.svg";
import ServiceIcon from "../../../../../../images/common/service.svg";
import ProductIcon from "../../../../../../images/common/product.svg";
import EditIcon from "../../../../../../images/products/edit-blue.svg";
import DonationIcon from "../../../../../../images/donation/donating-hands.svg";
import DeleteIcon from "../../../../../../images/products/trash-blue.svg";
import DeleteItemPopup from "./DeleteItemPopup";
import { debounceTime, Subscription } from "rxjs";
import {
  ActiveInactiveButton,
  AuthService,
  FlexContainer,
  ITableHeaders,
  Loader,
  ModalComponent,
  RedButton,
  StyledTooltip,
  TableHeader,
  ToastService,
  UtilityMethods,
  withRouter,
} from "@mindmemobile/mm-shared-global";
import {
  AccountSettingsStore,
  ItemsStore,
  RolesAndPermissionStore,
} from "../../../shared/store";
import {
  DonationService,
  ItemService,
  MembershipService,
  ProductService,
  ServiceAPIService,
} from "../../../shared/services";
import {
  GridNames,
  ItemBillingType,
  ItemDropDownOptions,
  PricingModel,
  SystemItemTypes,
} from "../../../shared/enums";
import {
  DashboardTopBar,
  DashboardTopRightSide,
  ItemContentArea,
  TableGrid,
} from "../../../shared/components";
import { MerchantAccountStore } from "../../../modules/merchantAccounts";

interface IProps {
  navigate: NavigateFunction;
  params: Readonly<Params<string>>;
  itemStore: ItemsStore;
  rolesAndPermissionStore: RolesAndPermissionStore;
  merchantAccountStore: MerchantAccountStore;
}

interface IState {
  deletePopUpLoader: boolean;
  firstTimeLoad: boolean;
}

@observer
class Items extends React.Component<IProps, IState> {
  private loader: Loader = new Loader(true);
  //private deletePopUpLoader = true;
  private searchSubscription: Subscription | null = null;
  //private firstTimeLoad: boolean = true;
  constructor(props: IProps) {
    super(props);
    this.state = {
      deletePopUpLoader: true,
      firstTimeLoad: true,
    };
    this.searchSubscription = new Subscription();
  }

  get itemStore() {
    return this.props.itemStore;
  }

  get rolesAndPermissionStore() {
    return this.props.rolesAndPermissionStore;
  }

  componentDidMount(): void {
    this.itemStore.updateSearchValue("", ItemDropDownOptions.Items);
    this.loadData(
      this.itemStore.itemGridPagination.pageNumber,
      this.itemStore.itemGridPagination.pageSize,
      this.itemStore.searchValue,
      true
    );
    this.searchSubscription = this.itemStore.searchSubject
      .pipe(debounceTime(500))
      .subscribe(() => {
        this.loadData(
          this.itemStore.itemGridPagination.pageNumber,
          this.itemStore.itemGridPagination.pageSize,
          this.itemStore.searchValue
        );
      });
  }

  componentWillUnmount(): void {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
  }

  loadData(
    pageNumber: number,
    pageSize: number,
    searchValue: string,
    firstTimeLoad: boolean = false
  ) {
    if (this.state.firstTimeLoad !== firstTimeLoad) {
      this.setState({
        firstTimeLoad: firstTimeLoad,
      });
    }
    this.loader.show();
    ItemService.getItems(
      AuthService.authData.accountId,
      pageNumber,
      pageSize,
      searchValue
    ).subscribe({
      next: (result) => {
        this.loader.hide();
        this.itemStore.deserializeGridData(result.result || [], result.meta);
      },
      error: (error) => {
        this.loader.hide();
        console.error(error);
      },
    });
  }

  onPaginationChange = (pageNumber: number, pageSize: number) => {
    this.loadData(pageNumber, pageSize, this.itemStore.searchValue);
  };

  getIcon(type: keyof typeof SystemItemTypes) {
    switch (SystemItemTypes[type]) {
      case "Product":
        return <img src={ProductIcon} className="img-20" />;
      case "Membership":
        return <img src={TrioBallsIcon} className="img-20" />;
      case "Service":
        return <img src={ServiceIcon} className="img-20" />;
      case "Donation":
        return <img src={DonationIcon} className="img-20" />;
      default:
        return <img src={""} className="img-20" />;
    }
  }

  getTitle(type: keyof typeof SystemItemTypes) {
    switch (SystemItemTypes[type]) {
      case "Product":
        return "Product";
      case "Membership":
        return "Membership";
      case "Service":
        return "Service";
      case "Donation":
        return "Donation";
      default:
        return type.toString();
    }
  }

  onEdit(id: string, type: keyof typeof SystemItemTypes) {
    if (!this.rolesAndPermissionStore.hasManagePermission("Edit")) return;
    switch (type) {
      case SystemItemTypes.Product:
        this.props.navigate(`/app/product/detail/${id}`);
        break;
      case SystemItemTypes.Membership:
        this.props.navigate(`/app/membership/detail/${id}`);
        break;
      case SystemItemTypes.Service:
        this.props.navigate(`/app/service/detail/${id}`);
        break;
      case SystemItemTypes.Donation:
        this.props.navigate(`/app/donation/detail/${id}`);
        break;
      default:
        break;
    }
  }

  onDelete(id: string, type: keyof typeof SystemItemTypes) {
    if (!this.rolesAndPermissionStore.hasManagePermission("Delete")) return;
    this.itemStore.setSelectedItemDetails(type, id);
    this.itemStore.toggleShowProductDeletePopup();

    this.setState({
      deletePopUpLoader: true,
    });

    switch (type) {
      case SystemItemTypes.Product:
        ProductService.getProductReferenceInPurchases(
          AuthService.authData.accountId,
          id
        ).subscribe({
          next: (result) => {
            this.itemStore.setPurchasedItemDetails(result.result);
            ProductService.getProduct(
              AuthService.authData.accountId,
              id
            ).subscribe({
              next: (result) => {
                if (!result.result) {
                  console.log("error");
                } else {
                  this.setState({
                    deletePopUpLoader: false,
                  });
                  this.itemStore.constructProductItemModel(
                    result.result,
                    false
                  );
                }
              },
            });
          },
        });
        break;
      case SystemItemTypes.Service:
        ServiceAPIService.getServiceReferenceInPurchases(
          AuthService.authData.accountId,
          id
        ).subscribe({
          next: (result) => {
            this.itemStore.setPurchasedItemDetails(result.result);
            ServiceAPIService.getService(
              AuthService.authData.accountId,
              id
            ).subscribe({
              next: (result) => {
                if (!result.result) {
                  console.log("error");
                } else {
                  this.setState({
                    deletePopUpLoader: false,
                  });
                  this.itemStore.constructServiceItemModel(
                    result.result,
                    false
                  );
                }
              },
            });
          },
        });
        break;
      case SystemItemTypes.Membership:
        MembershipService.getMembershipReferenceInPurchases(
          AuthService.authData.accountId,
          id
        ).subscribe({
          next: (result) => {
            this.itemStore.setPurchasedItemDetails(result.result);
            MembershipService.getMembership(
              AuthService.authData.accountId,
              id
            ).subscribe({
              next: (result) => {
                if (!result.result) {
                  console.log("error");
                } else {
                  this.setState({
                    deletePopUpLoader: false,
                  });
                  this.itemStore.constructMembershipItemModel(
                    result.result,
                    false
                  );
                }
              },
            });
          },
        });
        break;
      case SystemItemTypes.Donation:
        DonationService.geDonationReferenceInPurchases(
          AuthService.authData.accountId,
          id
        ).subscribe({
          next: (result) => {
            this.itemStore.setPurchasedItemDetails(result.result);
            DonationService.getDonation(
              AuthService.authData.accountId,
              id
            ).subscribe({
              next: (result) => {
                if (!result.result) {
                  console.log("error");
                } else {
                  this.setState({
                    deletePopUpLoader: false,
                  });
                  this.itemStore.constructDonationItemModel(
                    result.result,
                    false
                  );
                }
              },
            });
          },
        });
        break;
      default:
        break;
    }
  }

  goToDetailPage(id: string, type: keyof typeof SystemItemTypes, name: string) {
    this.props.navigate(`/app/items/detail/${id}/${type}/${name}`);
  }

  getPriceToShow(prices: PricingPageInputModel[]): string {
    const defaultPrice = prices?.find((price) => price.isDefault);

    if (!defaultPrice) return "-";

    switch (ItemBillingType[defaultPrice.billingType]) {
      case "Term":
        return this.getPriceFromSettings(defaultPrice.termSettings);
      case "Recurring":
        return this.getPriceFromSettings(defaultPrice.recurringSettings);
      case "TermPlusRecurring":
        return this.getPriceFromSettings(defaultPrice.termSettings);
      default:
        return "-";
    }
  }

  getPriceFromSettings(
    terms:
      | PricePageTermsInputModel
      | PricePageRecurringInputModel
      | null
      | undefined
  ): string {
    if (!terms) return "-";

    switch (terms.pricingModel) {
      case PricingModel.Standard:
        return !terms.standardPricing?.price
          ? "-"
          : `$${AccountSettingsStore.formatAmount(parseFloat(terms.standardPricing.price))}`;
      case PricingModel.Package:
        return !terms.packagePricing?.options
          ? "-"
          : `$${AccountSettingsStore.formatAmount(parseFloat(terms.packagePricing?.options[0]?.price))}`;
      case PricingModel.Volume:
        return !terms.volumePricing?.price
          ? "-"
          : `$${AccountSettingsStore.formatAmount(parseFloat(terms.volumePricing?.price))}`;
      case PricingModel.Tiered:
        return !terms.tieredPricing?.price
          ? "-"
          : `$${AccountSettingsStore.formatAmount(parseFloat(terms.tieredPricing?.price))}`;
      case PricingModel.Custom:
        return !terms.customPricing?.minimum
          ? "-"
          : `$${AccountSettingsStore.formatAmount(parseFloat(terms.customPricing.minimum))}`;
      default:
        return "-";
    }
  }

  get columns() {
    const columns: ITableHeaders<ItemGridModel>[] = [
      {
        dataKey: "isActive",
        title: "Status",
        className: "table-col-10",
        cellClassName: "table-col-10",
        onCellRender: (data: ItemGridModel) => {
          return (
            <ActiveInactiveButton
              // fontSize="12px"
              // fontWeight="600"
              minWidth="80px"
              inActive={!data.isActive}
            >
              {data.isActive ? "Active" : "Inactive"}
            </ActiveInactiveButton>
          );
        },
      },
      {
        dataKey: "createdDate",
        title: "Created",
        className: "table-col-10",
        cellClassName: "table-col-10",
        onCellRender: (data: ItemGridModel) => {
          return UtilityMethods.formatMMDDYYYY(data.createdDate);
        },
      },
      {
        dataKey: "type",
        title: "Type",
        className: "table-col-10",
        cellClassName: "table-col-10",
        onCellRender: (data: ItemGridModel) => {
          return (
            <span title={this.getTitle(data.type)}>
              {this.getIcon(data.type)}
            </span>
          );
        },
      },
      {
        dataKey: "name",
        title: "Item Name",
        cellColor: "#556bb8",
        className: "table-col-30",
        cellClassName: "table-col-30",
        onCellRender: (data: ItemGridModel) => {
          return data.name;
        },
      },
      {
        dataKey: "prices",
        title: "Price",
        className: "table-col-15",
        cellClassName: "table-col-15",
        onCellRender: (data: ItemGridModel) => {
          return this.getPriceToShow(data.prices || []);
        },
      },
      {
        dataKey: "onHandInventory",
        title: "On Hand",
        className: "table-col-15",
        cellClassName: "table-col-15",
        onCellRender: (data: ItemGridModel) => {
          return data.onHandInventory || "-";
        },
      },
      {
        dataKey: "totalVariants",
        title: "Variants",
        className: "table-col-10",
        cellClassName: "table-col-10",
        onCellRender: (data: ItemGridModel) => {
          return data.totalVariants || "-";
        },
      },
      {
        dataKey: "totalVariants",
        title: "Actions",
        className: "table-col-10 text-center",
        cellClassName: "table-col-10 text-center",
        onCellClick: (e) => {
          e.stopPropagation();
        },
        onCellRender: (data: ItemGridModel) => {
          const disableDeleteButton = data.type === SystemItemTypes.Donation;
          return (
            <FlexContainer
              alignItems="center"
              justifyContent="center"
              spacing={10}
            >
              <StyledTooltip tooltipText="Edit">
                <img
                  src={EditIcon}
                  alt="Edit"
                  onClick={(e) => {
                    e.stopPropagation();
                    this.onEdit(data.id, data.type);
                  }}
                  className="img-15 clickable"
                />
              </StyledTooltip>
              <StyledTooltip tooltipText="Delete">
                <img
                  src={DeleteIcon}
                  alt="Delete"
                  onClick={(e) => {
                    e.stopPropagation();
                    this.onDelete(data.id, data.type);
                  }}
                  className={`img-15 clickable`}
                />
              </StyledTooltip>
            </FlexContainer>
          );
        },
      },
    ];

    return columns;
  }

  renderDeletePopUpContent = () => {
    const {
      selectedProduct,
      selectedService,
      selectedMembership,
      selectedDonation,
    } = this.itemStore;
    switch (this.itemStore.selectedItemDetails?.type) {
      case SystemItemTypes.Product:
        return (
          <DeleteItemPopup
            selectedItem={selectedProduct}
            itemType={SystemItemTypes.Product}
            isLoading={this.state.deletePopUpLoader}
            purchasedItemDetails={this.itemStore.purchasedItemDetails}
          />
        );
      case SystemItemTypes.Service:
        return (
          <DeleteItemPopup
            selectedItem={selectedService}
            itemType={SystemItemTypes.Service}
            isLoading={this.state.deletePopUpLoader}
            purchasedItemDetails={this.itemStore.purchasedItemDetails}
          />
        );
      case SystemItemTypes.Membership:
        return (
          <DeleteItemPopup
            selectedItem={selectedMembership}
            itemType={SystemItemTypes.Membership}
            isLoading={this.state.deletePopUpLoader}
            purchasedItemDetails={this.itemStore.purchasedItemDetails}
          />
        );
      case SystemItemTypes.Donation:
        return (
          <DeleteItemPopup
            selectedItem={selectedDonation}
            itemType={SystemItemTypes.Donation}
            isLoading={this.state.deletePopUpLoader}
            purchasedItemDetails={this.itemStore.purchasedItemDetails}
          />
        );
      default:
        return null;
    }
  };

  onDeleteItem() {
    const { selectedItemDetails } = this.itemStore;
    if (!selectedItemDetails) return;

    const serviceMap = {
      Product: ProductService,
      Service: ServiceAPIService,
      Membership: MembershipService,
      Donation: DonationService,
    };

    const service = serviceMap[selectedItemDetails.type];
    if (service) {
      service
        .deleteItem(AuthService.authData.accountId, selectedItemDetails.id)
        .subscribe({
          next: (result) => {
            if (result.result) {
              ToastService.showSuccess(
                `${selectedItemDetails.type} deleted successfully`
              );
              this.loadData(
                this.itemStore.itemGridPagination.pageNumber,
                this.itemStore.itemGridPagination.pageSize,
                ""
              );
              this.itemStore.toggleShowProductDeletePopup();
            } else {
              console.log("Error deleting item");
            }
          },
        });
    }
  }

  isNoData(): boolean {
    return (
      this.state.firstTimeLoad &&
      this.itemStore.searchValue === "" &&
      this.itemStore.itemGridPagination.totalItemCount === 0 &&
      !this.loader.showLoader
    );
  }

  renderDetails(node: React.ReactNode, showItemRightContent = true) {
    const content = showItemRightContent ? (
      <DashboardTopRightSide
        itemStore={this.itemStore}
        showSearchInput={!this.isNoData()}
        showButton={true}
      />
    ) : (
      <></>
    );

    return (
      <>
        <DashboardTopBar
          itemStore={this.itemStore}
          rightContent={
            <>
              {this.loader.showLoader && this.state.firstTimeLoad
                ? null
                : content}
            </>
          }
          merchantAccountStore={this.props.merchantAccountStore}
          rolesAndPermissionStore={this.props.rolesAndPermissionStore}
        />
        <ItemContentArea
          itemStore={this.itemStore}
          node={node}
          showMerchantAccountNotificationBar={this.props.merchantAccountStore.hasNotificationBar()}
          showLoader={this.loader.showLoader}
          // className={this.isNoData() ? "content-area-only" : ""}
        />
      </>
    );
  }

  get body() {
    return (
      <>
        <TableGrid
          height={
            this.itemStore.showItemsFilter
              ? "calc(100vh - 499px)"
              : this.props.merchantAccountStore.hasNotificationBar()
                ? "calc(100vh - 350px)"
                : ""
          }
          tableHeader={
            <TableHeader
              showFilter={this.itemStore.showItemsFilter}
              onToggleFilter={() => this.itemStore.toggleItemsFilter()}
              total={this.itemStore.itemGridPagination.totalItemCount}
            />
          }
          totalItemCount={this.itemStore.itemGridPagination.totalItemCount}
          onRowClick={(data: ItemGridModel) =>
            this.goToDetailPage(data.id, data.type, data.name)
          }
          cellSpacing={20}
          onPaginationChange={(pageNumber: number, pageSize: number) =>
            this.onPaginationChange(pageNumber, pageSize)
          }
          pagination={this.itemStore.itemGridPagination}
          data={this.itemStore.itemsGridData}
          headers={this.columns}
          name={GridNames.Item}
          hideTable={this.isNoData()}
          rolesAndPermissionStore={this.rolesAndPermissionStore}
            countriesWithState={this.itemStore.countryStore.countriesWithState}
        />
        {this.itemStore.showProductDeletePopUp && (
          <ModalComponent
            title="Delete Item"
            titleFontSize="22px"
            bodyPadding="45px 60px"
            showFooter={true}
            minHeight="240px"
            wrapperWidth="650px"
            bodyBackgroundColor="#fefefe"
            footerBackground="rgba(255, 0, 0, 0.1)"
            footerHeight="auto"
            redHeader={true}
            blueHeader={false}
            onClose={() => this.itemStore.toggleShowProductDeletePopup()}
            footerContent={
              <FlexContainer
                alignItems="center"
                justifyContent="center"
                spacing={15}
              >
                <RedButton
                  minWidth="120px"
                  height="40px"
                  onClick={() => this.onDeleteItem()}
                >
                  Delete
                </RedButton>
                <RedButton
                  primary
                  minWidth="120px"
                  height="40px"
                  onClick={() => this.itemStore.toggleShowProductDeletePopup()}
                >
                  Do Not Delete
                </RedButton>
              </FlexContainer>
            }
          >
            {this.renderDeletePopUpContent()}
          </ModalComponent>
        )}
      </>
    );
  }

  render() {
    return this.renderDetails(this.body);
  }
}

export default withRouter(Items);
